import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { DocumentSigned } from '@stratumn/icons';

import Table from 'components/ui/table';
import { setAtPath, formatNumber } from 'utils';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/shadcn/dialog';

// data editor reader component
const DataEditorReader = memo(({ classes, dataEditor, data }) => {
  const {
    modifiedDataKey = 'changes',
    initialDataKey = 'rows',
    modifiedDataTitle = 'Edited Data',
    table: tableConfig
  } = dataEditor;

  const { [modifiedDataKey]: modifiedData, [initialDataKey]: initialData } =
    data;

  // if initial data has not been saved with the form just display empty initial data with the correct indexing
  const dataSelectorPath = tableConfig.dataSelectorPath || 'rowId';
  const tableData = useMemo(
    () =>
      initialData ||
      Object.keys(modifiedData).map(key => {
        const rowData = {};
        setAtPath(rowData, dataSelectorPath, key);
        return rowData;
      }),
    [initialData, modifiedData, dataSelectorPath]
  );

  const tableUpdate = useMemo(
    () => ({
      edit: {
        modifiedData,
        allowDisplayDiffs: true
      }
    }),
    [modifiedData]
  );

  const nbChangesMsg = `${formatNumber(
    tableData.length
  )} rows have been modified.`;

  return (
    <div>
      <div className={classes.readerItemTitle}>{modifiedDataTitle}</div>
      <div className={classes.readerInterfaceContainer}>
        <Dialog>
          <DialogTrigger asChild>
            <div
              className={classnames(
                classes.readerInterfaceItem,
                classes.readerInterface
              )}
              data-cy="data-editor-view-changes"
            >
              <div className={classes.readerInterfaceLink}>
                <DocumentSigned className={classes.readerInterfaceIcon} />
                <div>
                  <div className={classes.readerInterfaceInfoHeader}>View</div>
                  <div className={classes.readerInterfaceInfoSubHeader}>
                    edits
                  </div>
                </div>
              </div>
            </div>
          </DialogTrigger>
          <DialogContent className="w-[calc(100vw-2rem)] max-w-none">
            <DialogHeader>
              <DialogTitle>
                <div className={classes.readerModalTitle}>
                  <div>{modifiedDataTitle}</div>
                  <div className={classes.readerModalTitleSubHeader}>
                    {nbChangesMsg}
                  </div>
                </div>
              </DialogTitle>
            </DialogHeader>

            <div className="text-foreground overflow-y-auto text-[13px] leading-[1.25]">
              <div className="size-full overflow-auto">
                <div className={classes.readerModalBody}>
                  <Table
                    data={tableData}
                    config={tableConfig}
                    update={tableUpdate}
                  />
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <div
          className={classnames(
            classes.readerInterfaceItem,
            classes.readerInterfaceInfoSubHeader
          )}
          data-cy="data-editor-changes-count"
        >
          {nbChangesMsg}
        </div>
      </div>
    </div>
  );
});

DataEditorReader.propTypes = {
  classes: PropTypes.object.isRequired,
  dataEditor: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired
};

export default DataEditorReader;
